import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { fadeInAnimation } from '../../animations/fade-in.animation';
import { _DEFAULT_NUTRITIONAL_PROPERTIES, IFoodGroupIngredient } from '../../models/food-group';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { MessageAlertService } from '../../services/message-alert/message-alert.service';
import { BlockPeriodClass } from '../../class/meal-plan/block-period.class';
import { cloneDeep } from 'lodash';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { GuidService } from '../../services/guid/guid.service';
import { IngredientCalculationService } from '../../services/ingredient-calculation/ingredient-calculation.service';
import { IIngredient, IngredientsAction, IngredientState } from '../../models/ingredient';
import { IMeasurementUnit } from '../../models/measurement';
import { Logger } from '../../class/helper/logger.class';
import { MealPlanTypeEnum } from '../../models/meal-plans';


@Component({
  selector: 'app-individual-form',
  templateUrl: './individual-form.component.html',
  styleUrls: ['./individual-form.component.scss'],
  animations: [fadeInAnimation]
})
export class IndividualFormComponent implements OnInit, OnChanges, OnDestroy {
  showSelectIngredients: boolean;
  confirmModal?: NzModalRef;
  measureUnits: IMeasurementUnit[];
  loadingIngredient: boolean;

  @Input() mealPlanType: MealPlanTypeEnum;
  @Input() blockPeriod: BlockPeriodClass = new BlockPeriodClass(null, this.guidService.guid(), true);

  @Output() add: EventEmitter<BlockPeriodClass> = new EventEmitter();
  @Output() changeBlockPeriod: EventEmitter<{ blockPeriod: BlockPeriodClass, action: 'add' | 'edit' | 'delete' | 'cancel' }> = new EventEmitter();


  constructor(
    private store: Store,
    private messageAlertService: MessageAlertService,
    private modal: NzModalService,
    private guidService: GuidService,
    private ingredientCalculationService: IngredientCalculationService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.getMeasureUnits();
  }

  /**
   * Obtiene las unidades de medida desde redux.
   */
  async getMeasureUnits(): Promise<void> {
    this.measureUnits = cloneDeep(await firstValueFrom(this.store.selectOnce(IngredientState.getMeasureUnits)));
  }

  /**
   * Detecta cuando se cambia la propiedad blockPeriod para establecerle los datos y mostrarlos.
   * @param changes cambios
   */
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!changes.blockPeriod) return;
    if (!this.blockPeriod._id) this.blockPeriod.setId(this.guidService.guid());
  }


  /**
   * cuando se cancela se determina si era en modo edición, en ese caso se regresan los valores a como estaban al principio
   * en el otro caso es cuando aún no se ha agregado un block period y se cancela
   */
  onCancel(): void {
      this.changeBlockPeriod.emit({ blockPeriod: this.blockPeriod, action: 'cancel' });
  }


  /**
   * Emite al componente padre cuando se quiere agregar un nuevo block period o se modifica.
   */
  async onEmitPutBlockPeriod(): Promise<void> {
    if (this.blockPeriod.editMode) {
      this.changeBlockPeriod.emit({ blockPeriod: this.blockPeriod, action: 'edit' });
      this.blockPeriod.setEditMode(false);
      return;
    }
    this.add.emit(this.blockPeriod);
  }

  /**
   * Agregar en block period el ingrediente que se quiere modificar.
   * @param ingredient ingrediente
   */
  onSetIngredientToEdit(ingredient: Partial<IFoodGroupIngredient>): void {
    if (this.blockPeriod.ingredientToEdit?.ingredientId !== ingredient.ingredientId) {
      this.blockPeriod.ingredientToEdit = ingredient;
    } else {
      this.blockPeriod.ingredientToEdit = null;
    }
  }

  /**
   * Cuando se quiere mostrar la información de un ingrediente en el formulario.
   */
  async onShowFormIngredient(): Promise<void> {
    try {
      if (!this.blockPeriod.ingredientToEdit) {
        this.showSelectIngredients = true;
        return;
      }
      this.loadingIngredient = true;
      let ingredient: IIngredient;

      await firstValueFrom(this.store.dispatch(
        new IngredientsAction.SetSelectedIdUserListIngredient(
          this.blockPeriod.ingredientToEdit.ingredientId
        )));
      ingredient = cloneDeep(await firstValueFrom(this.store.selectOnce(IngredientState.getSelectedUserListIngredient)));

      if (!ingredient) {
        await firstValueFrom(this.store.dispatch(new IngredientsAction.GetById(this.blockPeriod.ingredientToEdit.ingredientId,
          {
            include: [{
              relation: 'unitOfMeasurement'
            },
            {
              relation: 'ingredientCategory'
            }]
          })));
        ingredient = cloneDeep(await firstValueFrom(this.store.selectOnce(IngredientState.getSelectedIngredient)));
      } else {
        await firstValueFrom((this.store.dispatch(new IngredientsAction.Set(ingredient))));
      }

      const nutrients = this.ingredientCalculationService.calculateNutritionalProperties(ingredient, this.measureUnits,
        this.blockPeriod.ingredientToEdit.measure?._id, this.blockPeriod.ingredientToEdit.units);

      this.blockPeriod.ingredientToEdit.nutritionalProperties = nutrients;
      this.blockPeriod.ingredientToEdit.category = ingredient.ingredientCategory;
      this.blockPeriod.ingredientToEdit.lastNutritionalProperties = nutrients;
      this.loadingIngredient = false;
      this.showSelectIngredients = true;
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    }
  }

  /**
   * Checa las propiedades del blockType
   */
  async onCheckPropertiesBlockTypeIndividual(): Promise<void> {
    if (!(this.blockPeriod.ingredientToEdit.nutritionalProperties && this.blockPeriod.ingredientToEdit.category)) {
      await this.onShowFormIngredient();
      return;
    }
    this.blockPeriod.ingredientToEdit.lastNutritionalProperties =
      cloneDeep(this.blockPeriod.ingredientToEdit.nutritionalProperties);
    await firstValueFrom(this.store.dispatch(new IngredientsAction.SetSelectedIdUserListIngredient(
      this.blockPeriod.ingredientToEdit.ingredientId
    )));
    const ingredient = cloneDeep(await firstValueFrom((this.store.selectOnce(IngredientState.getSelectedUserListIngredient))));
    await firstValueFrom((this.store.dispatch(new IngredientsAction.Set(ingredient))));
    this.showSelectIngredients = true;
  }

  /**
   * Emite al componente padre cuando agrega o modifica un ingrediente a la lista cuando es un food group
   * @param ingredient ingrediente
   */
  onEmitSaveIngredient(ingredient: Partial<IFoodGroupIngredient>): void {
    try {
      this.blockPeriod.setTitle(ingredient.ingredientName);
      if (!this.blockPeriod.ingredientToEdit) {
        this.blockPeriod.addIngredient(ingredient);
        this.blockPeriod.sumNutrients(ingredient.nutritionalProperties);
      } else {
        this.blockPeriod.editIngredient(ingredient, this.blockPeriod.ingredientToEdit.ingredientId);
        this.blockPeriod.subtractNutrients(ingredient.lastNutritionalProperties);
        this.blockPeriod.sumNutrients(ingredient.nutritionalProperties);
      }
      this.resetIngredientForm();
      this.changeBlockPeriod.emit({ blockPeriod: cloneDeep(this.blockPeriod), action: 'edit' });
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    }
  }

  /**
   * Descarta los cambios del formulario del ingrediente
   */
  onCancelEditIngredient(): void {
    this.resetIngredientForm();
  }

  /**
   * Elimina un ingrediente del food group
   * @param ingredient ingrediente object
   */
  async onRemoveIngredient(ingredient: Partial<IFoodGroupIngredient>): Promise<void> {
    if (this.blockPeriod.ingredients.length === 1) {
      this.messageAlertService.createMessage('warning', 'No se pueden eliminar todos los ingredientes, necesitas mínimo uno.', 5000);
      return;
    }
    this.confirmModal = this.modal.confirm({
      nzTitle: '¿Estás seguro de eliminar este ingrediente?',
      nzContent: 'Una vez guardado, esta acción no se podrá deshacer.',
      nzOkText: 'SÍ QUIERO ELIMINARLO',
      nzCancelText: 'NO QUIERO',
      nzOkDanger: true,
      nzClassName: 'modal-confirm modal-confirm-medium-size',
      nzClosable: false,
      nzOnOk: async () => {
        try {
          await firstValueFrom((this.store.dispatch(new IngredientsAction.GetById(ingredient.ingredientId))));
          const ingredientSelected = await firstValueFrom((this.store.selectOnce(IngredientState.getSelectedIngredient)));
          this.blockPeriod.deleteIngredient(this.blockPeriod.ingredientToEdit.ingredientId);
          const nutritionalPropertiesIngredient =
            this.ingredientCalculationService.calculateNutritionalProperties(
              ingredientSelected, this.measureUnits, ingredient.measure?._id, ingredient.units
            );
          this.blockPeriod.subtractNutrients(nutritionalPropertiesIngredient);
          this.resetIngredientForm();
          this.changeBlockPeriod.emit({ blockPeriod: cloneDeep(this.blockPeriod), action: 'edit' });
        } catch (e) {
          Logger.log('Ocurrio un error', e?.message);
        }
      },
      nzOnCancel: () => {
      }
    });
  }


  /**
   * Reestablece las variables para el listado de los ingredientes
   */
  resetIngredientForm(): void {
    this.blockPeriod.ingredientToEdit = null;
    this.showSelectIngredients = false;
    this.store.dispatch(new IngredientsAction.ResetIngredients());
  }

  ngOnDestroy(): void {
  }
}